import { useQuery, useMutation, useQueryClient, QueryObserverOptions } from "@tanstack/react-query";
import type { TimeReportListResponse } from "@models/TimeReport";
import { apiGet, apiPut } from "../client";

interface CommonQueryOptions {
	keepPreviousData?: QueryObserverOptions["keepPreviousData"];
	onSuccess?: QueryObserverOptions["onSuccess"];
	refetchOnWindowFocus?: boolean;
}

/**
 * Hook to get time reports with optional filters
 */
export function useTimeReports(
	customerId?: string,
	jobId?: string,
	onError?: (errorCode: string) => void,
	opts: CommonQueryOptions = {},
) {
	return useQuery<TimeReportListResponse>({
		queryKey: ["time-reports", customerId, jobId],
		queryFn: () =>
			apiGet<TimeReportListResponse>("/time-report", {
				customer: customerId || "",
				job: jobId || "",
				group: "true",
				expand: "shift,job,job.customer,consultant,totalReportedTime,totalInvoicedTime,weekStart,weekEnd,weekNo,year,dates",
			}),
		...opts,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to update a time report entry
 */
export function useUpdateTimeReport(onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ entryId, body }: { entryId: string; body: any }) => apiPut(`/time-report/${entryId}`, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["time-reports"] });
		},
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to get job reports with optional filters
 */
export function useJobReports(
	customerId?: string,
	jobId?: string,
	onError?: (errorCode: string) => void,
	opts: CommonQueryOptions = {},
) {
	const { data, ...rest } = useQuery<any>({
		queryKey: ["job-reports", customerId, jobId],
		queryFn: () =>
			apiGet<any>("/job-report", {
				customerId: customerId || "",
				jobId: jobId || "",
				expand: "consultant,job.customer",
			}),
		...opts,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		...data,
		...rest,
	};
}

/**
 * Hook to get a single job report by ID
 */
export function useJobReport(
	jobReportId?: string,
	onError?: (errorCode: string) => void,
	opts: CommonQueryOptions = {},
) {
	const { data, ...rest } = useQuery<any>({
		queryKey: ["job-reports", jobReportId],
		queryFn: () =>
			apiGet<any>(`/job-report/${jobReportId}`, {
				expand: "consultant,job.customer",
			}),
		...opts,
		enabled: !!jobReportId,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		jobReport: data,
		...rest,
	};
}
